import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material";
import { useNotification } from "components/NotificationProvider";

import { toE164Phone } from "code/phoneValidation";
import CommunicationPreferences from "components/communicationPreferences/CommunicationPreferences";
import PhoneInput from "components/PhoneInput";
import ResponsiveButton from "components/ResponsiveButton";
import SelectInput from "components/SelectInput";
import TextInput from "components/TextInput";

import { PERSONNEL_STATUS_LABELS, PersonnelStatus } from "enums/PersonnelEnums";

import { useAuth } from "@sal/authentication.react";
import {
	AddPersonnelUserFormValues,
	createUpsertPersonnelSchema,
	isCommunicationsOnlyGroup,
	isDriverOnlyRole
} from "components/personnelAndFleet/personnel/UpsertPersonnelSchema";
import {
	FORCED_CHANNELS,
	REQUIRED_ONE_OF_CHANNELS,
	REQUIRED_ONE_OF_ROWS
} from "constants/communicationPreferences";
import {
	NotificationType,
	useCreatePersonnelUser,
	useEditPersonnelUserWithPrefs,
	useGetNotificationTypes,
	useGetPersonnelUserDetail
} from "hooks/personnelUsers";
import { useGetRoles } from "hooks/roles";
import { PersonnelUser } from "models/PersonnelUser";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

const isForced = (systemName: string, field: string) =>
	FORCED_CHANNELS[systemName]?.includes(field) ?? false;

type ChannelValues = { portal: boolean; email: boolean; sms: boolean };

const applyRequiredChannelDefault = (
	systemName: string,
	channels: ChannelValues
): ChannelValues => {
	const required = REQUIRED_ONE_OF_CHANNELS[systemName];
	if (!required || required.some((channel) => channels[channel as keyof ChannelValues])) {
		return channels;
	}
	const defaultChannel = (
		required.includes("email") ? "email" : required[0]
	) as keyof ChannelValues;
	return { ...channels, [defaultChannel]: true };
};

// Satisfies the cross-type rules (in-portal on at least one unconfirmed reminder) by turning the
// channel on for the first type of each group that is actually present.
const applyRequiredRowDefaults = <T extends Record<string, ChannelValues>>(prefs: T): T => {
	for (const rule of REQUIRED_ONE_OF_ROWS) {
		const present = rule.systemNames.filter((systemName) => systemName in prefs);
		if (!present.length || present.some((systemName) => prefs[systemName][rule.channel])) {
			continue;
		}
		prefs[present[0]][rule.channel] = true;
	}
	return prefs;
};

const buildDefaultCommPrefs = (types: NotificationType[]) =>
	applyRequiredRowDefaults(
		Object.fromEntries(
			types.map(({ notificationTypeId, systemName }) => [
				systemName,
				{
					notificationTypeId,
					...applyRequiredChannelDefault(systemName, {
						portal: isForced(systemName, "portal"),
						email: isForced(systemName, "email"),
						sms: isForced(systemName, "sms")
					})
				}
			])
		)
	);

const buildCommPrefsPayload = (values: AddPersonnelUserFormValues) =>
	Object.values(values.communicationPreferences).map((channels) => ({
		notificationTypeId: channels.notificationTypeId,
		portal: channels.portal,
		email: channels.email,
		sms: channels.sms
	}));

interface UpsertPersonnelModalProps {
	open: boolean;
	onClose: () => void;
	user?: PersonnelUser | null;
	mode: "add" | "edit" | "duplicate";
}

export default function UpsertPersonnelModal({
	open,
	onClose,
	user = null,
	mode
}: UpsertPersonnelModalProps) {
	const theme = useTheme();
	const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));
	const isEditMode = mode === "edit";
	const isDuplicateMode = mode === "duplicate";
	const auth = useAuth();
	const canReadRoles = auth.userHasPermission("Roles", "Read");
	const { roleOptions, isLoading: isRolesLoading } = useGetRoles(open && canReadRoles);
	const { notificationTypes, isLoading: isTypesLoading } = useGetNotificationTypes(open);
	const { createPersonnelUser, isCreating } = useCreatePersonnelUser();
	const { editPersonnelUserWithPrefs, isEditing } = useEditPersonnelUserWithPrefs();
	const {
		userDetail,
		isLoading: isUserDetailLoading,
		error: userDetailError
	} = useGetPersonnelUserDetail(mode !== "add" && open ? user!.id : null);
	const isModalLoading = mode !== "add" ? isUserDetailLoading : isTypesLoading;
	const commPrefsData = userDetail?.communicationPreferences ?? null;
	const { showNotification } = useNotification();

	// Notification types come from the database only — if neither call returns any, show none.
	const displayRows =
		mode !== "add" && !isUserDetailLoading && commPrefsData?.length
			? commPrefsData.map((p) => ({
					systemName: p.systemName,
					name: p.name,
					description: p.description
				}))
			: notificationTypes.map((t) => ({
					systemName: t.systemName,
					name: t.name,
					description: t.description
				}));

	const defaultFormValues = useMemo<AddPersonnelUserFormValues>(
		() => ({
			firstName: "",
			lastName: "",
			email: "",
			phoneNumber: "",
			roles: [],
			status: "" as PersonnelStatus,
			communicationPreferences: buildDefaultCommPrefs(notificationTypes)
		}),
		[notificationTypes]
	);

	const initialFormValues = useMemo(
		() => ({
			...defaultFormValues,
			firstName: isDuplicateMode ? "" : (user?.firstName ?? ""),
			lastName: isDuplicateMode ? "" : (user?.lastName ?? ""),
			email: isDuplicateMode ? "" : (user?.email ?? ""),
			phoneNumber: isDuplicateMode ? "" : (user?.fullPhone ?? ""),
			roles: user?.roles ?? [],
			status:
				user?.active !== undefined
					? user.active
						? PersonnelStatus.ACTIVE
						: PersonnelStatus.DEACTIVATED
					: ("" as PersonnelStatus)
		}),
		[defaultFormValues, user, isDuplicateMode]
	);

	const schema = useMemo(() => createUpsertPersonnelSchema(mode), [mode]);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		trigger,
		formState: { isSubmitted, isValid, isDirty, dirtyFields }
	} = useForm<AddPersonnelUserFormValues>({
		defaultValues: initialFormValues,
		resolver: zodResolver(schema),
		mode: "onBlur"
	});

	const selectedRoles = useWatch({ control, name: "roles" });
	const isGroupMode = isCommunicationsOnlyGroup(selectedRoles ?? []);
	const isDriverOnly = isDriverOnlyRole(selectedRoles ?? []);

	// Communications Only-only contacts have no last name (the group name lives in First Name) —
	// clear any stale last name so it doesn't get silently resubmitted once the field is hidden.
	useEffect(() => {
		if (isGroupMode) {
			setValue("lastName", "");
		}
	}, [isGroupMode, setValue]);

	const dialogContentRef = useRef<HTMLDivElement | null>(null);

	const scrollToFirstValidationError = useCallback(() => {
		requestAnimationFrame(() => {
			const container = dialogContentRef.current;
			if (container == null) {
				return;
			}

			const firstError = container.querySelector(
				[
					".MuiFormHelperText-root.Mui-error",
					"input[aria-invalid='true']",
					"textarea[aria-invalid='true']",
					".MuiSelect-select[aria-invalid='true']",
					".MuiFormControl-root.Mui-error",
					"[data-comm-pref-error]"
				].join(", ")
			);

			if (firstError instanceof HTMLElement) {
				firstError.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
			}
		});
	}, []);

	useEffect(() => {
		if (userDetailError) {
			showNotification({ message: "Unable to load personnel data", severity: "error" });
		}
	}, [userDetailError, showNotification]);

	useEffect(() => {
		if (open) {
			reset(initialFormValues);
		}
	}, [open, reset, initialFormValues]);

	useEffect(() => {
		if (!open || !commPrefsData || commPrefsData.length === 0) {
			return;
		}

		const loadedPrefs = Object.fromEntries(
			commPrefsData.map((pref) => [
				pref.systemName,
				{
					notificationTypeId: pref.notificationTypeId,
					...applyRequiredChannelDefault(pref.systemName, {
						portal: isForced(pref.systemName, "portal") || pref.portal,
						email: isForced(pref.systemName, "email") || pref.email,
						sms: isForced(pref.systemName, "sms") || pref.sms
					})
				}
			])
		);

		reset((current) => ({ ...current, communicationPreferences: loadedPrefs }), {
			keepDirty: false,
			keepErrors: false
		});
	}, [open, commPrefsData, reset]);

	useEffect(() => {
		if (mode !== "add" || !open || notificationTypes.length === 0) {
			return;
		}

		reset(
			(current) => ({
				...current,
				communicationPreferences: buildDefaultCommPrefs(notificationTypes)
			}),
			{ keepDirty: false, keepErrors: false }
		);
	}, [mode, open, notificationTypes, reset]);

	const showPartialSaveWarning = (message: string) => {
		showNotification({
			message: `User details were saved, but communication preferences could not be fully saved: ${message}`,
			severity: "warning",
			autoHideDuration: 7000
		});
	};

	const handleFormSubmit = async (values: AddPersonnelUserFormValues) => {
		const isUserDirty =
			!isEditMode || Object.keys(dirtyFields).some((field) => field !== "communicationPreferences");
		const isCommPrefsDirty = !isEditMode || "communicationPreferences" in dirtyFields;

		const userPayload = {
			firstName: values.firstName,
			lastName: values.lastName,
			email: values.email,
			phoneNumber: values.phoneNumber,
			roles: values.roles,
			active: values.status === PersonnelStatus.ACTIVE
		};

		if (isEditMode) {
			if (!isUserDirty && !isCommPrefsDirty) {
				onClose();
				return;
			}

			const result = await editPersonnelUserWithPrefs(user!.id, {
				...userPayload,
				communicationPreferences: buildCommPrefsPayload(values)
			});

			if (!result.success) {
				showNotification({
					message: result.errorMessage || "Unable to save personnel data",
					severity: "error"
				});
				return;
			}

			if (result.warningMessage) {
				showPartialSaveWarning(result.warningMessage);
			} else {
				showNotification({ message: "Personnel member updated successfully", severity: "success" });
			}
		} else {
			const result = await createPersonnelUser({
				...userPayload,
				communicationPreferences: buildCommPrefsPayload(values)
			});

			if (!result.success) {
				showNotification({
					message: result.errorMessage || "Unable to save personnel data",
					severity: "error"
				});
				return;
			}

			if (result.warningMessage) {
				showPartialSaveWarning(result.warningMessage);
			} else {
				showNotification({ message: "Personnel member added successfully", severity: "success" });
			}
		}

		onClose();
	};

	const handleCancel = () => {
		onClose();
	};

	const handleExit = () => {
		reset(defaultFormValues);
	};

	return (
		<Dialog
			open={open}
			onClose={isModalLoading ? undefined : handleCancel}
			maxWidth="sm"
			fullWidth
			fullScreen={isFullScreen}
			slotProps={{ transition: { onExited: handleExit } }}
		>
			<DialogTitle
				variant="text20Bold"
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					p: 2,
					borderBottom: 1,
					borderColor: "divider"
				}}
			>
				{isEditMode ? "Edit user" : "Add user"}
				<IconButton
					onClick={handleCancel}
					size="small"
					aria-label="Close"
					disabled={isModalLoading}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent ref={dialogContentRef} sx={{ p: 0, position: "relative" }}>
				<Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ p: { xs: 2, md: 4 } }}>
					<Grid size={12}>
						<Typography variant="h2">Personal details</Typography>
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="firstName"
							control={control}
							render={({ field, fieldState }) => (
								<TextInput
									label={isGroupMode ? "Group name" : "First name"}
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									disabled={isModalLoading}
									required
								/>
							)}
						/>
					</Grid>
					{!isGroupMode && (
						<Grid size={{ xs: 12, sm: 6 }}>
							<Controller
								name="lastName"
								control={control}
								render={({ field, fieldState }) => (
									<TextInput
										label="Last name"
										value={field.value}
										onChange={field.onChange}
										onBlur={field.onBlur}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
										disabled={isModalLoading}
										required
									/>
								)}
							/>
						</Grid>
					)}

					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="email"
							control={control}
							render={({ field, fieldState }) => (
								<TextInput
									label="Email"
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									error={!!fieldState.error}
									helperText={
										fieldState.error?.message ??
										(!isEditMode && isDriverOnly ? "Optional for Driver-only users" : undefined)
									}
									disabled={isEditMode || isModalLoading} // Email should not be editable after creation
									required={!isEditMode && !isDriverOnly}
									labelTooltip={
										isEditMode ? "Email cannot be edited after user creation" : undefined
									}
									valueTooltip={isEditMode ? field.value || undefined : undefined}
								/>
							)}
						/>
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="phoneNumber"
							control={control}
							render={({ field, fieldState }) => (
								<PhoneInput
									label="Phone number"
									value={field.value}
									onChange={field.onChange}
									onBlur={() => {
										const val = field.value?.trim() ?? "";
										if (val) {
											field.onChange(toE164Phone(val));
										}
										field.onBlur();
									}}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									disabled={isModalLoading}
								/>
							)}
						/>
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="status"
							control={control}
							render={({ field, fieldState }) => (
								<SelectInput
									label="Status"
									options={[
										{
											label: PERSONNEL_STATUS_LABELS[PersonnelStatus.ACTIVE],
											value: PersonnelStatus.ACTIVE
										},
										{
											label: PERSONNEL_STATUS_LABELS[PersonnelStatus.DEACTIVATED],
											value: PersonnelStatus.DEACTIVATED
										}
									]}
									placeholder="Select a status"
									value={field.value}
									onChange={field.onChange}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									disabled={isModalLoading}
									required
								/>
							)}
						/>
					</Grid>

					<Grid size={12}>
						<Controller
							name="roles"
							control={control}
							render={({ field, fieldState }) => (
								<SelectInput
									multiple
									label="Role(s)"
									options={roleOptions}
									loading={isRolesLoading}
									value={field.value}
									onChange={field.onChange}
									placeholder="Select roles"
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									disabled={isModalLoading}
									required
								/>
							)}
						/>
					</Grid>

					{!canReadRoles && (
						<Grid size={12}>
							<Alert severity="info" variant="outlined">
								You do not have permission to view roles. Please contact your administrator.
							</Alert>
						</Grid>
					)}

					<Grid size={12}>
						<Divider />
					</Grid>

					<Grid size={12}>
						<Typography variant="h2">Communication preferences</Typography>
					</Grid>

					<Grid size={12}>
						<CommunicationPreferences
							control={control}
							setValue={setValue}
							trigger={trigger}
							rows={displayRows}
							isLoading={mode !== "add" && isUserDetailLoading}
						/>
					</Grid>
				</Grid>
				{isModalLoading && (
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: "rgba(255, 255, 255, 0.6)",
							zIndex: 1
						}}
					>
						<CircularProgress />
					</Box>
				)}
			</DialogContent>
			<DialogActions
				disableSpacing
				sx={{
					borderTop: 1,
					borderColor: "divider",
					flexDirection: { xs: "column-reverse", sm: "row" },
					justifyContent: "flex-end",
					gap: 2,
					p: 2
				}}
			>
				<ResponsiveButton variant="text" onClick={handleCancel} disabled={isModalLoading}>
					Cancel
				</ResponsiveButton>
				<ResponsiveButton
					variant="contained"
					onClick={() => handleSubmit(handleFormSubmit, scrollToFirstValidationError)()}
					loading={isCreating || isEditing}
					disabled={
						isModalLoading ||
						(isEditMode
							? !isDirty || (isSubmitted && !isValid)
							: isDuplicateMode
								? (!isDriverOnly && !dirtyFields.email) || (isSubmitted && !isValid)
								: isSubmitted && !isValid)
					}
				>
					{isEditMode ? "Save updates" : "Add user"}
				</ResponsiveButton>
			</DialogActions>
		</Dialog>
	);
}
